import { useState } from 'react';
import axios from 'axios';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Stack,
  Paper,
  Card,
  CardContent,
  ToggleButton,
  ToggleButtonGroup,
  List,
  ListItem,
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip,
  Divider,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  AccountTree,
  TextFields,
  FormatListBulleted,
  AccountTree as FlowIcon,

  ZoomIn,
  ZoomOut,
  Refresh,
  CloudUpload
} from '@mui/icons-material';
import { generateContent } from '../utils/ai';

// Sample mind map data for demonstration
const sampleMindMap = {
  central_idea: "React Mind Map Visualization",
  branches: [
    {
      title: "View Types",
      sub_branches: [
        {
          title: "ASCII Tree View",
          details: "Text-based representation using ASCII characters"
        },
        {
          title: "Card-based View",
          details: "Hierarchical cards with visual styling"
        },
        {
          title: "Classified/List View",
          details: "Indented list with bullets and hierarchy"
        },
        {
          title: "Flow Chart View",
          details: "Vertical flow chart with connected nodes"
        }
      ]
    },
    {
      title: "Features",
      sub_branches: [
        {
          title: "Theme Support",
          details: "Light and dark mode compatibility"
        },
        {
          title: "Responsive Design",
          details: "Adapts to different screen sizes"
        },
        {
          title: "View Toggling",
          details: "Easy switching between visualization types"
        }
      ]
    },
    {
      title: "Implementation",
      sub_branches: [
        {
          title: "React",
          details: "Component-based architecture"
        },
        {
          title: "Material UI",
          details: "Styling and UI components"
        },
        {
          title: "Data Structure",
          sub_branches: [
            {
              title: "Title",
              details: "Main concept name"
            },
            {
              title: "Details",
              details: "Optional additional information"
            },
            {
              title: "Branches",
              details: "Nested child nodes"
            }
          ]
        }
      ]
    }
  ]
};

// ASCII Tree View Component
const AsciiTreeView = ({ node, prefix = '', isLast = true, isRoot = true }) => {

  const getNodeSymbols = (isLast, isRoot) => {
    if (isRoot) return '';
    return isLast ? '└── ' : '├── ';
  };

  const getChildPrefix = (prefix, isLast) => {
    return prefix + (isLast ? '    ' : '│   ');
  };

  const renderNode = () => {
    const nodeSymbol = getNodeSymbols(isLast, isRoot);
    const title = node.title || node.central_idea;
    return (
      <Box component="pre" sx={{
        fontFamily: 'monospace',
        whiteSpace: 'pre-wrap',
        m: 0,
        p: 0,
        color: 'text.primary'
      }}>
        {prefix + nodeSymbol + '[' + title + ']'}
        {node.details && (
          <Box component="pre" sx={{
            fontFamily: 'monospace',
            whiteSpace: 'pre-wrap',
            m: 0,
            ml: 4,
            p: 0,
            color: 'text.secondary',
            fontSize: '0.9em'
          }}>
            {prefix + (isRoot ? '' : '    ') + '- ' + node.details}
          </Box>
        )}
      </Box>
    );
  };

  const renderChildren = () => {
    const children = [...(node.branches || []), ...(node.sub_branches || [])];
    return children.map((child, index) => (
      <AsciiTreeView
        key={index}
        node={child}
        prefix={getChildPrefix(prefix, isLast)}
        isLast={index === children.length - 1}
        isRoot={false}
      />
    ));
  };

  return (
    <Box>
      {renderNode()}
      {renderChildren()}
    </Box>
  );
};

// Card-based Mind Map Node Component
const MindMapNode = ({ node, depth = 0 }) => {
  const theme = useTheme();

  const getNodeColors = (depth) => {
    if (theme.palette.mode === 'dark') {
      // Dark mode colors
      const darkColors = [
        theme.palette.primary.dark,
        theme.palette.primary.main,
        theme.palette.primary.light,
        theme.palette.secondary.dark
      ];
      return {
        bg: darkColors[depth % darkColors.length],
        text: theme.palette.primary.contrastText,
        secondaryText: 'rgba(255, 255, 255, 0.7)'
      };
    } else {
      // Light mode colors
      const lightColors = ['#e3f2fd', '#bbdefb', '#90caf9', '#64b5f6'];
      return {
        bg: lightColors[depth % lightColors.length],
        text: theme.palette.text.primary,
        secondaryText: theme.palette.text.secondary
      };
    }
  };

  return (
    <Box sx={{ ml: depth * 4, mb: 2 }}>
      <Card
        elevation={2}
        sx={{
          bgcolor: getNodeColors(depth).bg,
          color: getNodeColors(depth).text,
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: 6,
            transform: 'translateY(-2px)'
          }
        }}
      >
        <CardContent>
          <Typography variant={depth === 0 ? 'h5' : 'h6'} gutterBottom>
            {node.title || node.central_idea}
          </Typography>
          {node.details && (
            <Typography variant="body2" sx={{
              color: getNodeColors(depth).secondaryText
            }}>
              {node.details}
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Render sub-branches */}
      {node.sub_branches && node.sub_branches.length > 0 && (
        <Box sx={{ ml: 2 }}>
          {node.sub_branches.map((branch, index) => (
            <MindMapNode key={index} node={branch} depth={depth + 1} />
          ))}
        </Box>
      )}

      {/* Render branches */}
      {node.branches && node.branches.length > 0 && (
        <Box sx={{ ml: 2 }}>
          {node.branches.map((branch, index) => (
            <MindMapNode key={index} node={branch} depth={depth + 1} />
          ))}
        </Box>
      )}
    </Box>
  );
};

// Classified View Component
const ClassifiedView = ({ node }) => {

  const renderBranch = (branch, level) => {
    return (
      <Box key={branch.title || branch.central_idea} sx={{ ml: level * 2, mb: 2 }}>
        <Typography
          variant={level === 0 ? 'h5' : level === 1 ? 'h6' : 'body1'}
          sx={{
            fontWeight: level < 2 ? 'bold' : 'normal',
            color: level === 0 ? 'primary.main' : 'text.primary',
            mb: 1
          }}
        >
          {level === 0 ? '● ' : level === 1 ? '○ ' : '• '}
          {branch.title || branch.central_idea}
        </Typography>

        {branch.details && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ ml: 2, mb: 1 }}
          >
            {branch.details}
          </Typography>
        )}

        <List dense={level > 1}>
          {[...(branch.branches || []), ...(branch.sub_branches || [])].map((subBranch, index) => (
            <ListItem key={index} sx={{ display: 'block', pb: 0 }}>
              {renderBranch(subBranch, level + 1)}
            </ListItem>
          ))}
        </List>
      </Box>
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      {renderBranch(node, 0)}
    </Box>
  );
};

// Flow View Component
const FlowView = ({ node }) => {
  const theme = useTheme();

  const renderBranch = (branch, level) => {
    const children = [...(branch.branches || []), ...(branch.sub_branches || [])];
    const hasChildren = children.length > 0;

    return (
      <Box sx={{ width: '100%', mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            position: 'relative',
          }}
        >
          {/* Vertical connector line from parent */}
          {level > 0 && (
            <Box
              sx={{
                position: 'absolute',
                top: -20,
                height: 20,
                width: 2,
                bgcolor: 'primary.main',
                opacity: 0.5,
              }}
            />
          )}

          {/* Node content */}
          <Card
            elevation={3}
            sx={{
              minWidth: 200,
              maxWidth: 300,
              bgcolor: level === 0 ? 'primary.main' : theme.palette.mode === 'dark' ? 'background.paper' : 'background.default',
              color: level === 0 ? 'primary.contrastText' : 'text.primary',
              textAlign: 'center',
              position: 'relative',
              zIndex: 1,
              border: theme.palette.mode === 'dark' && level !== 0 ? `1px solid ${theme.palette.divider}` : 'none',
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: 6,
                transform: 'translateY(-2px)'
              }
            }}
          >
            <CardContent>
              <Typography variant={level === 0 ? 'h5' : 'h6'}>
                {branch.title || branch.central_idea}
              </Typography>
              {branch.details && (
                <Typography
                  variant="body2"
                  sx={{
                    color: level === 0 ? 'primary.contrastText' : 'text.secondary',
                    mt: 1
                  }}
                >
                  {branch.details}
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Children container */}
          {hasChildren && (
            <>
              {/* Vertical line down to children */}
              <Box
                sx={{
                  width: 2,
                  height: 20,
                  bgcolor: 'primary.main',
                  opacity: 0.5,
                  my: 1,
                }}
              />

              {/* Horizontal line above children */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  width: '100%',
                  position: 'relative',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    height: 2,
                    bgcolor: 'primary.main',
                    opacity: 0.5,
                    width: `${Math.min(100, children.length * 50)}%`,
                  }}
                />
              </Box>

              {/* Children */}
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                  gap: 4,
                  width: '100%',
                  mt: 2,
                }}
              >
                {children.map((child, index) => (
                  <Box
                    key={index}
                    sx={{
                      flex: '1 1 300px',
                      maxWidth: 300,
                      minWidth: 200,
                    }}
                  >
                    {renderBranch(child, level + 1)}
                  </Box>
                ))}
              </Box>
            </>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ p: 2, width: '100%', overflowX: 'auto' }}>
      {renderBranch(node, 0)}
    </Box>
  );
};

// Main MindMap Component
function MindMap() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [viewMode, setViewMode] = useState('card'); // 'card', 'ascii', 'classified', or 'flow'
  const [mindMapData, setMindMapData] = useState(sampleMindMap);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [file, setFile] = useState(null);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Toggle between view modes
  const handleViewModeChange = (_, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  // Zoom in/out functionality
  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.1, 1.5));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.1, 0.5));
  };

  // Reset zoom
  const handleResetZoom = () => {
    setZoomLevel(1);
  };

  // Handle file upload
  const handleFileUpload = (event) => {
    const uploadedFile = event.target.files[0];
    if (uploadedFile && (uploadedFile.type === 'application/pdf' || uploadedFile.type === 'text/plain')) {
      setFile(uploadedFile);
      setError('');
    } else {
      setError('Please upload a PDF or text file');
    }
  };

  // Generate mind map using Together AI
  const handleGenerateMindMap = async () => {
    if (!file && !text.trim()) {
      setError('Please upload a file or enter text');
      return;
    }

    setLoading(true);
    setError('');

    try {
      let content = text;

      if (file) {
        if (file.type === 'text/plain') {
          content = await file.text();
        } else {
          try {
            // For PDF files, use pdfjs-dist
            const pdf = await import('pdfjs-dist');
            pdf.GlobalWorkerOptions.workerSrc = new URL(
              'pdfjs-dist/build/pdf.worker.mjs',
              import.meta.url
            ).href;
            const arrayBuffer = await file.arrayBuffer();
            const loadingTask = pdf.getDocument({ data: arrayBuffer });
            const pdfDoc = await loadingTask.promise;

            let extractedText = '';
            for (let i = 1; i <= pdfDoc.numPages; i++) {
              const page = await pdfDoc.getPage(i);
              const textContent = await page.getTextContent();
              extractedText += textContent.items.map(item => item.str).join(' ');
            }
            content = extractedText;
          } catch (pdfError) {
            console.error('PDF processing error:', pdfError);
            setError('Failed to extract text from PDF. Please try a text file instead.');
            setLoading(false);
            return;
          }
        }
      }

      // Use generateContent for AI call
      const messages = [
        {
          role: 'system',
          content: `You are a mind map generator. Create detailed, hierarchical mind maps in JSON format. Follow this structure exactly:
          {
            "central_idea": "Main Topic",
            "branches": [
              {
                "title": "Key Concept",
                "sub_branches": [
                  {
                    "title": "Sub-concept",
                    "details": "Brief explanation"
                  }
                ]
              }
            ]
          }
          Ensure the output is valid JSON. Make concepts concise but informative. Include meaningful relationships between concepts.`
        },
        {
          role: 'user',
          content: `Generate a detailed mind map from this content. Format it as specified JSON structure:\n\n${content}`
        }
      ];
      const messageContent = await generateContent(messages);
      console.log('Response content:', messageContent);

      // Try to find JSON content within the response
      let jsonStr = messageContent;
      let mindMapJson;

      try {
        // First try to parse the entire response
        mindMapJson = JSON.parse(jsonStr);
      } catch (parseError) {
        // If that fails, try to extract JSON portion
        if (messageContent.includes('{')) {
          try {
            jsonStr = messageContent.substring(messageContent.indexOf('{'), messageContent.lastIndexOf('}') + 1);
            mindMapJson = JSON.parse(jsonStr);
          } catch (extractError) {
            console.error('JSON parsing error:', extractError);
            throw new Error('Could not parse AI response as valid JSON. Please try again with different content.');
          }
        } else {
          throw new Error('No JSON object found in AI response. Please try again.');
        }
      }

      // Validate the structure
      if (!mindMapJson.central_idea || !Array.isArray(mindMapJson.branches)) {
        throw new Error('Invalid mind map structure. Please try again with different content.');
      }

      setMindMapData(mindMapJson);
      setFile(null);
      setText('');
    } catch (err) {
      const errorMessage = err.response?.data?.error?.message || err.response?.data?.error || err.message || 'Failed to generate mind map';
      setError(errorMessage);
      console.error('API Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const generateMindMap = async (content) => {
    try {
      const messages = [
        {
          role: 'system',
          content: `You are a mind map generator. Create detailed, hierarchical mind maps in JSON format. Follow this structure exactly:
          {
            "central_idea": "Main Topic",
            "branches": [
              {
                "title": "Key Concept",
                "sub_branches": [
                  {
                    "title": "Sub-concept",
                    "details": "Brief explanation"
                  }
                ]
              }
            ]
          }
          Ensure the output is valid JSON. Make concepts concise but informative. Include meaningful relationships between concepts.`
        },
        {
          role: 'user',
          content: `Generate a detailed mind map from this content. Format it as specified JSON structure:\n\n${content}`
        }
      ];

      return await generateContent(messages);
    } catch (error) {
      console.error('Error generating mind map:', error);
      throw error;
    }
  };

  return (
    <Box sx={{ py: 4 }}>
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant={isMobile ? "h4" : "h2"} gutterBottom>
            Mind Map Visualizer
          </Typography>
        </Box>

        <Stack spacing={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Upload Document or Enter Text
            </Typography>
            <Stack spacing={2}>
              <Button
                component="label"
                variant="outlined"
                startIcon={<CloudUpload />}
                sx={{ width: 'fit-content' }}
              >
                Upload File
                <input
                  type="file"
                  onChange={handleFileUpload}
                  hidden
                  accept=".pdf,.txt"
                />
              </Button>
              {file && (
                <Typography variant="body2" color="text.secondary">
                  Selected file: {file.name}
                </Typography>
              )}

              <TextField
                fullWidth
                multiline
                rows={4}
                placeholder="Or enter your topic or text here..."
                value={text}
                onChange={(e) => setText(e.target.value)}
                disabled={loading}
              />

              {error && (
                <Alert severity="error" onClose={() => setError('')}>
                  {error}
                </Alert>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  onClick={handleGenerateMindMap}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Generate Mind Map'}
                </Button>
              </Box>
            </Stack>
          </Paper>

          <Paper sx={{ p: 3, mb: 4 }}>
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="h6">
              Visualization Options
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={handleViewModeChange}
                size="small"
                aria-label="mind map view mode"
              >
                <ToggleButton value="card" aria-label="card view">
                  <Tooltip title="Card View">
                    <AccountTree />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="ascii" aria-label="ascii view">
                  <Tooltip title="ASCII Tree View">
                    <TextFields />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="classified" aria-label="classified view">
                  <Tooltip title="List View">
                    <FormatListBulleted />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="flow" aria-label="flow view">
                  <Tooltip title="Flow Chart View">
                    <FlowIcon sx={{ transform: 'rotate(90deg)' }} />
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>

              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

              <Tooltip title="Zoom out">
                <IconButton onClick={handleZoomOut} size="small" color="primary">
                  <ZoomOut />
                </IconButton>
              </Tooltip>

              <Typography variant="body2" sx={{ minWidth: '3em', textAlign: 'center' }}>
                {Math.round(zoomLevel * 100)}%
              </Typography>

              <Tooltip title="Zoom in">
                <IconButton onClick={handleZoomIn} size="small" color="primary">
                  <ZoomIn />
                </IconButton>
              </Tooltip>

              <Tooltip title="Reset zoom">
                <IconButton onClick={handleResetZoom} size="small">
                  <Refresh />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Box sx={{
            mt: 2,
            bgcolor: viewMode === 'ascii' ? (theme.palette.mode === 'dark' ? 'background.paper' : 'grey.100') : 'transparent',
            p: 2,
            borderRadius: 1,
            border: viewMode === 'classified' || viewMode === 'flow' ? 1 : 0,
            borderColor: 'divider',
            maxWidth: viewMode === 'flow' ? '100%' : undefined,
            overflowX: viewMode === 'flow' ? 'auto' : undefined,
            transform: `scale(${zoomLevel})`,
            transformOrigin: 'top left',
            transition: 'transform 0.3s ease'
          }}>
            {viewMode === 'card' ? (
              <MindMapNode node={mindMapData} />
            ) : viewMode === 'ascii' ? (
              <AsciiTreeView node={mindMapData} />
            ) : viewMode === 'classified' ? (
              <ClassifiedView node={mindMapData} />
            ) : (
              <FlowView node={mindMapData} />
            )}
          </Box>
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            About Mind Map Visualizer
          </Typography>
          <Typography variant="body1" paragraph>
            This component demonstrates four different ways to visualize a mind map structure:
          </Typography>
          <List>
            <ListItem>
              <Typography variant="body1">
                <strong>Card View:</strong> Hierarchical cards with visual styling and indentation
              </Typography>
            </ListItem>
            <ListItem>
              <Typography variant="body1">
                <strong>ASCII Tree View:</strong> Text-based representation using ASCII characters
              </Typography>
            </ListItem>
            <ListItem>
              <Typography variant="body1">
                <strong>List View:</strong> Indented list with bullets and hierarchy indicators
              </Typography>
            </ListItem>
            <ListItem>
              <Typography variant="body1">
                <strong>Flow Chart View:</strong> Vertical flow chart with connected nodes
              </Typography>
            </ListItem>
          </List>
          <Typography variant="body1" paragraph>
            The component supports responsive design, theme adaptation for light/dark modes, and zoom functionality.
          </Typography>
        </Paper>
        </Stack>
      </Container>
    </Box>
  );
}

export default MindMap;